import React from "react";
import { FiCalendar, FiClock, FiLock, FiUnlock } from "react-icons/fi";
import { format, isValid } from "date-fns";

const formatPeriodDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return isValid(date) ? format(date, "d MMM yyyy") : "-";
};

const TimesheetPeriodStatusCard = ({ range, status, submission, loading, error }) => {
  const lockReason = String(status?.lockReason || "").toLowerCase();
  const submissionStatus = String(submission?.status || status?.submissionStatus || "").toLowerCase();
  const unlockReason = submission?.unlockReason || status?.unlockReason || "";
  const isPending = lockReason === "pending";
  const isApproved = lockReason === "approved" && status?.canEdit !== true;
  const isUnlocked =
    (submissionStatus === "unlocked" || lockReason === "unlocked") &&
    status?.canEdit === true;
  const isReopened = Boolean(status?.isReopened) && status?.canEdit === true;
  const isExpired = Boolean(status?.isExpired);

  let title = "กรอกและส่ง Timesheet ได้";
  let description = `กรอกและส่งได้ภายในวันที่ ${formatPeriodDate(status?.deadline)}`;
  let icon = <FiUnlock />;
  let tone = "border-emerald-200 bg-emerald-50/70 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-500/10 dark:text-emerald-200";

  if (loading) {
    title = "กำลังตรวจสอบกำหนดส่ง...";
    description = "";
    icon = <FiClock className="animate-spin" />;
    tone = "border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300";
  } else if (error) {
    title = "ไม่สามารถตรวจสอบกำหนดส่งได้";
    description = error;
    icon = <FiLock />;
    tone = "border-red-200 bg-red-50/70 text-red-700 dark:border-red-900/60 dark:bg-red-500/10 dark:text-red-200";
  } else if (isPending) {
    title = "รออนุมัติ";
    description = "ช่วงเวลานี้ถูกล็อกระหว่างรอการอนุมัติ";
    icon = <FiLock />;
    tone = "border-amber-200 bg-amber-50/70 text-amber-800 dark:border-amber-900/60 dark:bg-amber-500/10 dark:text-amber-200";
  } else if (isApproved) {
    title = "อนุมัติแล้ว";
    description = "Timesheet ช่วงนี้ถูกล็อกแล้ว";
    icon = <FiLock />;
    tone = "border-emerald-200 bg-emerald-50/70 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-500/10 dark:text-emerald-200";
  } else if (isUnlocked) {
    title = "ปลดล็อกให้แก้ไข";
    description = status?.reopenUntil
      ? `ผู้อนุมัติปลดล็อกให้แก้ไขและส่งใหม่ได้ถึง ${formatPeriodDate(status.reopenUntil)}`
      : "ผู้อนุมัติปลดล็อกให้แก้ไขและส่งใหม่ได้";
    icon = <FiUnlock />;
    tone = "border-indigo-200 bg-indigo-50/70 text-indigo-800 dark:border-indigo-900/60 dark:bg-indigo-500/10 dark:text-indigo-200";
  } else if (isReopened) {
    title = "เปิดให้แก้ไขย้อนหลัง";
    description = `เปิดให้แก้ไขและส่งได้ถึง ${formatPeriodDate(status?.reopenUntil)}`;
    icon = <FiUnlock />;
    tone = "border-indigo-200 bg-indigo-50/70 text-indigo-800 dark:border-indigo-900/60 dark:bg-indigo-500/10 dark:text-indigo-200";
  } else if (isExpired) {
    title = "หมดเวลาส่ง Timesheet";
    description = `กำหนดส่งเดิม: ${formatPeriodDate(status?.deadline)}`;
    icon = <FiLock />;
    tone = "border-red-200 bg-red-50/70 text-red-700 dark:border-red-900/60 dark:bg-red-500/10 dark:text-red-200";
  }

  return (
    <div className={`flex items-start gap-3 rounded-2xl border px-4 py-3 ${tone}`}>
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/70 dark:bg-gray-900/30">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="font-semibold">สถานะช่วงเวลา: {title}</p>
          {range?.from && range?.to && (
            <span className="text-xs opacity-80">
              <FiCalendar className="mr-1 inline" />
              {formatPeriodDate(range.from)} - {formatPeriodDate(range.to)}
            </span>
          )}
        </div>
        {description && <p className="mt-1 text-sm opacity-90">{description}</p>}
        {isUnlocked && unlockReason && (
          <p className="mt-1 text-sm opacity-90">เหตุผลที่ปลดล็อก: {unlockReason}</p>
        )}
        {isExpired && !isPending && !isApproved && !isReopened && (
          <p className="mt-1 text-xs opacity-90">
            กรุณาติดต่อผู้ดูแลระบบเพื่อเปิดให้แก้ไขย้อนหลัง
          </p>
        )}
      </div>
    </div>
  );
};

export default TimesheetPeriodStatusCard;
